import { useState } from 'react'
import styles from './NoAccess.module.css'

interface NoAccessProps {
  error?: string | null
}

const FEATURES: { icon: string; lead: string; detail: string }[] = [
  { icon: 'group',               lead: 'Built-in reviewer routing.',              detail: 'Auto-tags reviewers in channel, optional DMs, and auto-provisioned reviewer suggestions synced to GitHub activity.' },
  { icon: 'notifications_active',lead: 'All inbound review requests in one place.',detail: 'Across any number of teams and handles.' },
  { icon: 'filter_alt',          lead: 'Repo, label, and author filtering.',       detail: 'Allow or block by pattern — only what matters reaches the channel.' },
  { icon: 'psychology',          lead: 'Context-aware nudges.',                   detail: 'Right person, right moment, based on actual PR state.' },
  { icon: 'person_search',       lead: 'Stale PR alerts with a claim system.',    detail: 'Someone always owns the next move.' },
  { icon: 'escalator_warning',   lead: 'Automatic manager escalation.',           detail: 'Loops in leadership after configurable reminders go unanswered.' },
  { icon: 'schedule',            lead: 'Timezone and working-hours aware.',       detail: 'Silent outside your team\'s day.' },
  { icon: 'bar_chart',           lead: 'Weekly velocity digest.',                 detail: 'Week-over-week trends and top reviewer recognition.' },
]

export function NoAccess({ error }: NoAccessProps) {
  return (
    <div className={styles.page}>
      <div className={styles.card}>

        {/* Logo + title */}
        <div className={styles.logoWrap}>
          <img src="/logo.png" alt="Review Ops" className={styles.logo}
            onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
          />
        </div>
        <h1 className={styles.title}>Review Ops</h1>
        <p className={styles.tagline}>
          AI-powered PR review operations manager for Slack.
        </p>

        {/* Feature list */}
        <div className={styles.features}>
          {FEATURES.map(f => (
            <div key={f.icon} className={styles.feature}>
              <span className={`mi ${styles.featureIcon}`}>{f.icon}</span>
              <span>
                <span className={styles.featureLead}>{f.lead}</span>
                {' '}
                <span className={styles.featureDetail}>{f.detail}</span>
              </span>
            </div>
          ))}
        </div>

        <div className={styles.divider} />

        {error ? (
          /* ── Expired / invalid token ── */
          <>
            <div className={styles.errorBadge}>
              <span className="mi" style={{ fontSize: 16 }}>error_outline</span>
              {error}
            </div>
            <CommandBlock
              label="Get a new link"
              steps={[
                { detail: 'Open the Slack channel you want to configure' },
                { command: '/review-ops settings', detail: 'sends you a fresh link' },
              ]}
            />
          </>
        ) : (
          /* ── Unauthenticated landing ── */
          <>
            <div className={styles.paths}>
              <CommandBlock
                label="New channel"
                steps={[
                  { command: '/invite @Review-Ops', detail: 'in the channel' },
                  { command: '/review-ops onboard', detail: 'to get started' },
                ]}
              />
              <CommandBlock
                label="Existing channel"
                steps={[
                  { command: '/review-ops settings', detail: 'to configure your channel' },
                ]}
              />
            </div>
          </>
        )}

        <div className={styles.footer}>
          Access is scoped to channels you're a member of
        </div>
      </div>
    </div>
  )
}

interface StepDef {
  command?: string
  detail?: string
}

function CommandBlock({ label, steps }: { label: string; steps: StepDef[] }) {
  return (
    <div className={styles.block}>
      <div className={styles.blockLabel}>{label}</div>
      {steps.map((s, i) => (
        <div key={i} className={styles.step}>
          <div className={styles.stepNum}>{i + 1}</div>
          <div className={styles.stepText}>
            {s.command && <CopyCommand command={s.command} />}
            {s.detail && <span className={styles.stepDetail}>{s.detail}</span>}
          </div>
        </div>
      ))}
    </div>
  )
}

function CopyCommand({ command }: { command: string }) {
  const [copied, setCopied] = useState(false)

  function copy() {
    navigator.clipboard.writeText(command).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1800)
    })
  }

  return (
    <button className={styles.copyCmd} onClick={copy} title="Copy to clipboard">
      <code>{command}</code>
      <span className={`mi ${styles.copyIcon}`}>{copied ? 'check' : 'content_copy'}</span>
    </button>
  )
}
